import { createContext, useContext } from 'react';
import { APPROX_RATES, INR_TO, COUNTRY_ISO } from './constants';

// ── INR → foreign currency approximate conversion ────────────────────────────
export function inrToForeign(inr, iso) {
  const currency = APPROX_RATES[iso];
  const rate = currency && INR_TO[currency];
  if (!rate) return null;
  const amt = (inr * rate).toFixed(currency === 'KWD' || currency === 'BHD' || currency === 'OMR' ? 2 : 0);
  return `≈ ${currency} ${Number(amt).toLocaleString()}`;
}

// ── Country → flag emoji ──────────────────────────────────────────────────────
export function countryFlag(country) {
  if (!country) return null;
  const iso = COUNTRY_ISO[country.toLowerCase().trim()];
  if (!iso) return null;
  // Unicode regional indicator: A=0x1F1E6, flag = pair of regional indicators
  const flag = [...iso].map(c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)).join('');
  return { flag, iso, isAbroad: iso !== 'IN' };
}

// ── Auth header helper ────────────────────────────────────────────────────────
export function authHeader() {
  const t = localStorage.getItem('memberToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// ── sessionStorage cache (never stores sensitive/financial data) ──────────────
// The browser's HTTP cache (Cache-Control: private) is the primary cache layer.
// sessionStorage is a fallback for cases where the browser cache is bypassed
// (e.g. hard-refresh, incognito). Data lives only for the tab session.
export function ssGet(key, ttlMs) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { ts, d } = JSON.parse(raw);
    if (Date.now() - ts > ttlMs) { sessionStorage.removeItem(key); return null; }
    return d;
  } catch { return null; }
}

export function ssSet(key, data) {
  try { sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), d: data })); } catch {}
}

export function ssInvalidate(key) {
  try { sessionStorage.removeItem(key); } catch {}
}

// ── Shared notification sound (module-level AudioContext — unlocked on first user gesture) ──
let _audioCtx = null;
const _unlockAudio = () => {
  if (!_audioCtx) {
    try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch {}
  }
  if (_audioCtx && _audioCtx.state === 'suspended') _audioCtx.resume().catch(() => {});
};
// Unlock on any user gesture once
if (typeof window !== 'undefined') {
  ['click', 'keydown', 'touchstart'].forEach(ev =>
    window.addEventListener(ev, _unlockAudio, { once: false, passive: true })
  );
}

export function playNotifSound(type = 'message') {
  try {
    if (!_audioCtx) {
      try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch { return; }
    }
    const doPlay = () => {
      try {
        const osc  = _audioCtx.createOscillator();
        const gain = _audioCtx.createGain();
        osc.connect(gain);
        gain.connect(_audioCtx.destination);
        const t = _audioCtx.currentTime;
        if (type === 'direct') {
          osc.frequency.setValueAtTime(880, t);
          osc.frequency.setValueAtTime(1100, t + 0.13);
          gain.gain.setValueAtTime(0.25, t);
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
          osc.start(t); osc.stop(t + 0.4);
        } else if (type === 'festival') {
          osc.frequency.setValueAtTime(523, t);
          osc.frequency.setValueAtTime(659, t + 0.12);
          osc.frequency.setValueAtTime(784, t + 0.24);
          gain.gain.setValueAtTime(0.3, t);
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
          osc.start(t); osc.stop(t + 0.5);
        } else {
          osc.frequency.setValueAtTime(660, t);
          gain.gain.setValueAtTime(0.18, t);
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
          osc.start(t); osc.stop(t + 0.25);
        }
      } catch {}
    };
    if (_audioCtx.state === 'suspended') {
      _audioCtx.resume().then(doPlay).catch(() => {});
    } else {
      doPlay();
    }
  } catch {}
}

// ── Day-scoped sessionStorage helpers (auto-clear at midnight) ───────────────
export const TODAY = new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'

export function loadDaySet(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return new Set();
    const { date, ids } = JSON.parse(raw);
    if (date !== TODAY) { sessionStorage.removeItem(key); return new Set(); }
    return new Set(ids);
  } catch { return new Set(); }
}

export function saveDaySet(key, set) {
  try { sessionStorage.setItem(key, JSON.stringify({ date: TODAY, ids: [...set] })); } catch {}
}

// ── Language context ──────────────────────────────────────────────────────────
// Usage anywhere: const { t } = useLang();  then t('हिंदी', 'English')
export const LangCtx = createContext({ lang: 'hi', setLang: () => {} });
export const useLang = () => useContext(LangCtx);
// Standalone helper for components that receive lang as a prop
export const tl = (lang, hi, en) => lang === 'en' ? en : hi;

// ── Title-case helper ────────────────────────────────────────────────────────
export const toTitle = s => s ? s.replace(/\b\w/g, c => c.toUpperCase()) : s;

// ── Date and amount formatters ────────────────────────────────────────────────
export function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}
export function fmtAmount(n) {
  if (!n && n !== 0) return '—';
  return Number(n).toLocaleString('en-IN');
}

// ── Birthday check ────────────────────────────────────────────────────────────
export function isBirthdayToday(dob) {
  if (!dob) return false;
  try {
    const d   = new Date(dob);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  } catch { return false; }
}
